/**
 * 処理（Rule）の入力スキーマ — 7 欄 ＋ 形ごとの中身（NE §1-attrs の Rule）。
 *
 * 作り直しで変わるのは 1 点に集約できる。業務ロジックが自由文（規則 557 本）から、**出力を
 * ちょうど 1 つ持つ処理への参照の並び**になる。同じ文の規則が 23 本あり最大 19 業務に写って
 * いた（コピー 136 本）ため、文のままだと抜けも重なりも機械で数えられない。
 *
 * 型名の `Rule` は Business Rules Group の言うビジネスルールから採った。判定・計算・動作を
 * すべてビジネスルールに含める定義なので、処理の 4 形（判定表 / 式 / 並べ替え / 変更）を
 * 覆う。日本語の「処理」はそのまま使う（出典: `packages/domain/spec-graph/README.md`）。
 *
 * **業務の出力 1 つにつき処理 1 つである。** 粒度の歯止めは形ではなく出力の側で掛ける —
 * 同じ業務の中で同じ出力を 2 つの処理が決めていないかはリリースゲートが見る（制約 65）。割って
 * よいのは、その値を別の処理か別の業務が読むときだけである。
 *
 * 名前は「出力 ＋ 動詞」で作る（引き当て対象を判定する）。並べ替えだけは「母集合 ＋ 動詞」で
 * 作る（選定対象を並べる）。前半が出力なので、名前を見れば何を決める処理か分かる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 処理（Rule）の zod 入力スキーマ。出力・形 4 種の中身・実装・DB 参照・元の規則
 * @graph-connects none
 */
import { z } from 'zod';
import { implementedBySchema, usesColumnSchema, usesTableSchema } from '../node-schemas.js';
import { isSequentialLocalIds, localElementId, optionalSpecElementId, specElementId, } from './ids.js';
/**
 * 処理の形 4 種（NE §1-attrs）。形は属性で、処理を割る軸ではない。
 *
 * - `decisionTable` 判定表 — 入力の組から出力を決める表
 * - `formula` 式 — 数を計算する
 * - `sort` 並べ替え — 母集合に順序を付ける
 * - `mutation` 変更 — 対象の値を変える
 *
 * 外部サービスを呼ぶことは種別にしない。「与信を通す」は与信結果を判定する（判定表）、
 * 「決済結果をメールで送る」は決済結果の通知を作成する（変更）になる。外部かどうかは実装の
 * 参照に落ちる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 処理の形（判定表 / 式 / 並べ替え / 変更）の allowlist
 * @graph-connects none
 */
export const RULE_KINDS = ['decisionTable', 'formula', 'sort', 'mutation'];
/**
 * 処理の CRUD 分類。業務の CRUD は配下の処理の和として導出する。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 処理の CRUD 分類の allowlist
 * @graph-connects none
 */
export const RULE_CRUDS = ['C', 'R', 'U', 'D'];
/**
 * 「変更」の処理が取る CRUD。読みだけの `R` は変更ではないので入らない。
 *
 * 変更の処理が持つ 2 つの `crud`（`ruleCommonShape.crud` と `mutationBodySchema.crud`）が
 * 食い違う形（変更なのに `R`）は、**このスキーマでは検出しない** — 突き合わせる `refine` を
 * 置いていない。ファイル内の他の相互整合（制約 43 / 47 / 49 / 50）と違って制約の番号もまだ
 * 無く、検査は制約を作り直す段で `constraint-rules.ts` に足す。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 変更の処理が取る CRUD（C / U / D）の allowlist
 * @graph-connects none
 */
export const MUTATION_CRUDS = ['C', 'U', 'D'];
/**
 * 判定表の行の重なりの扱い 2 種。
 *
 * - `none` 重なりなし — どの入力の組も高々 1 行にしか当たらない
 * - `firstMatch` 上から先に当たった行 — 入力の値が同じ行が複数在りうる
 *
 * `firstMatch` を持てるようにしてあるから、行は行 ID を持つ必要がある（入力の値を鍵にすると
 * 案件の差分で行が黙って 1 つに潰れる）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 判定表の行の重なりの扱い（重なりなし / 上から先に当たった行）の allowlist
 * @graph-connects none
 */
export const DECISION_TABLE_OVERLAP_POLICIES = ['none', 'firstMatch'];
/**
 * 並べ替えの順序のうち、列挙で書く 4 種（API §3.3 の値）。
 *
 * - `desc` 高い順 / `asc` 低い順 — 数・量・金額・個数
 * - `earliest` 早い順 / `latest` 遅い順 — 日時
 *
 * 区分・状態・真偽の属性は列挙ではなく値の並び（用語の id の並び）で順序を書く。値どうしに
 * 大小が無いので、どの値を先に回すかを並びで決める。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 並べ替えの順序のうち列挙で書く 4 種（高い順 / 低い順 / 早い順 / 遅い順）の allowlist
 * @graph-connects none
 */
export const SORT_DIRECTIONS = ['desc', 'asc', 'earliest', 'latest'];
/**
 * 属性に値が無いときの扱い 3 種（API §3.3 の値）。`first` 先 / `last` 後 / `exclude` 除く。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 並べ替えの属性に値が無いときの扱い（先 / 後 / 除く）の allowlist
 * @graph-connects none
 */
export const SORT_MISSING_VALUE_POLICIES = ['first', 'last', 'exclude'];
/**
 * 全部の行で並びが決まらなかったとき（同順位）の扱い 2 種（API §3.3 の値）。
 *
 * - `undefined` 定めない
 * - `stable` 元の順を保つ（安定）
 *
 * 「定めない」を値として書かせるのは、書き忘れと「決めた結果として定めない」を分けるため
 * である。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 並べ替えの同順位の扱い（定めない / 元の順を保つ）の allowlist
 * @graph-connects none
 */
export const SORT_TIE_BREAKS = ['undefined', 'stable'];
/**
 * 判定表の範囲のセルの比べ方 5 種（設計書 NE §1 の判定表。書き方は仮で、NE §7 に未決として残る）。
 *
 * - `lt` より小さい / `lte` 以下 / `gt` より大きい / `gte` 以上 — 数と日時
 * - `sameDay` 同じ日 — 日時
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 判定表の範囲のセルの比べ方（より小さい / 以下 / より大きい / 以上 / 同じ日）の allowlist
 * @graph-connects none
 */
export const DECISION_TABLE_RANGE_COMPARATORS = ['lt', 'lte', 'gt', 'gte', 'sameDay'];
/**
 * 範囲のセルの比べる相手。数値そのもの（`literal`）か、別の入力の用語（`term`。実行の時点など）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 判定表の範囲のセルの比べる相手（数値 / 用語）の入力スキーマ
 * @graph-connects none
 */
export const decisionTableRangeOperandSchema = z.union([
    z.strictObject({ literal: z.number() }),
    z.strictObject({ term: specElementId() }),
]);
/**
 * 判定表の範囲のセル。比べ方 1 つと相手 1 つを持つ（`{ lte: { literal: 5 } }`）。
 *
 * 値の用語で書けない区切り（5 日以内・実行の時点より前）を、自由文でなく構造で書くための形で
 * ある。比べ方を鍵にするので、1 つのセルに比べ方は 1 つしか置けない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 判定表の範囲のセル（比べ方と相手）の入力スキーマ
 * @graph-connects none
 */
export const decisionTableRangeCellSchema = z.union(DECISION_TABLE_RANGE_COMPARATORS.map((comparator) => z.strictObject({ [comparator]: decisionTableRangeOperandSchema })));
/**
 * 判定表のセル 1 つ。値の用語の id か、`null`（「—」＝ その欄は見ない）か、範囲。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 判定表のセル 1 つ（値 / 見ない / 範囲）の入力スキーマ
 * @graph-connects none
 */
export const decisionTableCellSchema = z.union([
    specElementId(),
    z.null(),
    decisionTableRangeCellSchema,
]);
/**
 * 判定表の 1 行。セルは列のプロパティが持つ値の用語を指す。
 *
 * `id` は表の中で 1 から振る番号で、並べ替えても変わらない（決定。2026-09-21）。
 * `cells` は入力欄と同じ数・同じ順で並べ、その欄を見ないなら `null` を置く（「—」）。値の用語で
 * 書けない区切りは範囲のセルで書く。
 * `outputs` は出力欄と同じ数・同じ順で並べる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 判定表の 1 行（行 ID・入力セル・出力セル）の入力スキーマ
 * @graph-connects none
 */
export const decisionTableRowSchema = z.strictObject({
    id: localElementId(),
    cells: z.array(decisionTableCellSchema),
    outputs: z.array(specElementId()),
});
/**
 * 判定表の中身。入力欄と出力欄はプロパティの用語への参照の並びである。
 *
 * 各欄の取りうる値が用語の側から決まるので、行の抜けを機械で数えられる（制約 48）。業務の
 * 分岐は出力欄の取りうる値の数だけ出る（制約 43）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 判定表の中身（入力欄・出力欄・行・重なりの扱い）の入力スキーマ
 * @graph-connects none
 */
export const decisionTableBodySchema = z.strictObject({
    inputs: z.array(specElementId()).min(1),
    outputs: z.array(specElementId()).min(1),
    rows: z.array(decisionTableRowSchema).min(1),
    overlapPolicy: z.enum(DECISION_TABLE_OVERLAP_POLICIES),
});
/**
 * 式の演算子 4 種（NE §1-attrs の式）。
 *
 * 設計書の式は「参照 ＋ 演算子 ＋ 数値（単位つき）」で、演算は四則に閉じている。列挙で受ける
 * のは、加減（`add` / `subtract`）と乗除（`multiply` / `divide`）で単位の扱いが違うためで
 * ある — 加減は項の単位が一致していなければならず（制約 49）、乗除は単位が合成される。
 * 演算子が文字列だと、この区別を機械で付けられない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 式の演算子 4 種（加算 / 減算 / 乗算 / 除算）の allowlist
 * @graph-connects none
 */
export const FORMULA_OPERATORS = ['add', 'subtract', 'multiply', 'divide'];
/**
 * 式の項の条件（NE §1 の式）。そのプロパティがその値のときだけ項を足す（引く）。
 *
 * 当たらないとき、その項は足さない。条件のプロパティは業務ルールの入力に数える。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 式の項の条件（プロパティと値。当たるときだけ項を足す）の入力スキーマ
 * @graph-connects none
 */
export const formulaTermConditionSchema = z.strictObject({
    property: specElementId(),
    value: specElementId(),
});
/**
 * 式の項 3 種。どれを書いたかは持っている鍵で判別する。
 *
 * - `term` 用語（数 / 量 / 金額 / 個数の表現の型）への参照。ここから `USES_TERM` を導出する
 * - `step` 同じ式の上の段の中間の名前（局所 id）。下の段だけが上の段を指せるので循環を書けない
 * - `literal` 数値そのもの。`unit` に値が入るのは、その数値が量・金額・個数のように単位を伴う
 *   ときだけである（倍率のような無次元の数は空にする）
 *
 * **項を自由文にしない。** 文字列にすると項が用語への参照でなくなり、式から `USES_TERM` を
 * 導出できず、加減する項の単位が一致するか（制約 49）も読めない。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 式の項（用語への参照 / 上の段の中間の名前 / 単位つきの数値）の入力スキーマ
 * @graph-connects none
 */
export const formulaOperandSchema = z.union([
    z.strictObject({ term: specElementId(), when: formulaTermConditionSchema.optional() }),
    z.strictObject({ step: localElementId() }),
    z.strictObject({ literal: z.number(), unit: specElementId().optional() }),
]);
/**
 * 式そのもの。演算子 1 つと、それが束ねる項 2 つ以上。
 *
 * 項が 1 つの式は演算になっていない（中間の名前を別の名前へ写すだけ）ので、2 つ以上を要求
 * する。3 つ以上を許すのは、同じ演算子の連鎖（`a + b + c`）を段に割らせないためである。
 * 演算子が混ざる式は段を分けて書く — 段の順が上から下の一方向なので、括弧の入れ子を型に
 * 持たなくても評価の順が決まる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 式（演算子 1 つと項 2 つ以上）の入力スキーマ
 * @graph-connects none
 */
export const formulaExpressionSchema = z
    .strictObject({
    operator: z.enum(FORMULA_OPERATORS),
    operands: z.array(formulaOperandSchema).min(2),
})
    .superRefine((expression, context) => {
    if (expression.operator === 'add' || expression.operator === 'subtract')
        return;
    for (const [index, operand] of expression.operands.entries()) {
        if ('when' in operand && operand.when !== undefined) {
            context.addIssue({
                code: 'custom',
                path: ['operands', index, 'when'],
                message: '項の条件（when）は加減の項にだけ書ける',
            });
        }
    }
});
/**
 * 式の 1 段。中間の名前に、参照と演算子と数値で組んだ式の結果を置く。
 *
 * 下の段は上の段の中間の名前を使える。上から下の一方向にしか参照できないので、循環を書け
 * ない。式が構造で入っているので、加減する項の単位が一致すること（制約 49）は、項の用語から
 * 単位を引いて機械で判定できる。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 式の 1 段（中間の名前と式）の入力スキーマ
 * @graph-connects none
 */
export const formulaStepSchema = z.strictObject({
    id: localElementId(),
    name: specElementId(),
    expression: formulaExpressionSchema,
});
/**
 * 式の中身。段を上から順に並べ、最後に出す値のプロパティを指す。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 式の中身（段の並びと出す値）の入力スキーマ
 * @graph-connects none
 */
export const formulaBodySchema = z.strictObject({
    steps: z.array(formulaStepSchema).min(1),
    output: specElementId(),
});
/**
 * 並べ替えの行 1 本（優先, 属性, 順序, 値が無いとき）。
 *
 * `rank` は 1 から振る優先で、上の行が先に効く。`attribute` はプロパティの用語で、順序を
 * 持つ表現の型でなければならない（制約 47）。`order` は属性の表現の型で決まる 2 通りの
 * どちらかである — 区分・状態・真偽は値の用語の id の並び（先に書いた値を先に回す）、数と
 * 日時は列挙。どちらを書くべきかの突き合わせもリリースゲートが見る。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 並べ替えの行 1 本（優先・属性・順序・値が無いとき）の入力スキーマ
 * @graph-connects none
 */
export const sortKeySchema = z.strictObject({
    rank: localElementId(),
    attribute: specElementId(),
    order: z.union([z.array(specElementId()).min(1), z.enum(SORT_DIRECTIONS)]),
    whenMissing: z.enum(SORT_MISSING_VALUE_POLICIES),
});
/**
 * キーの決め方の判定表の 1 行。業務ルールの判定表の行と違い、出力は決めるキーの値 1 つである。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business キーの決め方の判定表の 1 行（行 ID・入力セル・キーの値）の入力スキーマ
 * @graph-connects none
 */
export const deriveDecisionTableRowSchema = z.strictObject({
    id: localElementId(),
    cells: z.array(decisionTableCellSchema),
    output: specElementId(),
});
/**
 * キーの決め方の判定表の中身。判定表の欄のまま、出力欄を持たない（出力 ＝ キー）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business キーの決め方の判定表の中身（入力欄・行・重なりの扱い）の入力スキーマ
 * @graph-connects none
 */
export const deriveDecisionTableBodySchema = z.strictObject({
    inputs: z.array(specElementId()).min(1),
    rows: z.array(deriveDecisionTableRowSchema).min(1),
    overlapPolicy: z.enum(DECISION_TABLE_OVERLAP_POLICIES),
});
/**
 * キーの決め方の式の中身。式の欄のまま、出す値を持たない（出す値 ＝ キー）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business キーの決め方の式の中身（段の並び）の入力スキーマ
 * @graph-connects none
 */
export const deriveFormulaBodySchema = z.strictObject({
    steps: z.array(formulaStepSchema).min(1),
});
/**
 * 並べ替えのキー 1 つの決め方（derive。NE §1 の並べ替え・API §3.3）。キーの値を、判定表か式で
 * 入力から決める。
 *
 * キーを決めるだけの判定は業務ルールに割らない（UI の合意 2026-09-27）。並べ替えの外の誰にも
 * 読まれず、「割ってよいのは、その値を別の業務ルールか別の業務が読むときだけ」に当たらない。
 * キーの値は並べ替えの中の値で、業務ルールの出力でも入力でもない。
 *
 * `key` が同じ並べ替えの行の属性に在ること、下の決め方だけが上の決め方の決めたキーを入力に
 * 使えることは、リリースゲートの制約 42 が見る（書込では止めない）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 並べ替えのキー 1 つの決め方（キー・判定表か式の中身）の入力スキーマ
 * @graph-connects none
 */
export const sortDeriveSchema = z.discriminatedUnion('kind', [
    z.strictObject({
        key: specElementId(),
        kind: z.literal('decisionTable'),
        body: deriveDecisionTableBodySchema,
    }),
    z.strictObject({
        key: specElementId(),
        kind: z.literal('formula'),
        body: deriveFormulaBodySchema,
    }),
]);
/**
 * 並べ替えの中身。母集合と行の並びと同順位の扱い、出す件数、キーの決め方。
 *
 * 行の優先は 1 から欠番なしで振る。欠番や重複があると、どの行が先に効くかが決まらない。
 *
 * `take` は並べた先頭から出す件数で、無ければ全件を出す。`derive` に無いキーは、入力の値を
 * そのまま使う。キーの決め方の判定表がどの行にも当たらないキーは値が無い扱いで、そのキーの
 * 行の `whenMissing` に従う。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 並べ替えの中身（母集合・行の並び・同順位の扱い・出す件数・キーの決め方）の入力スキーマ
 * @graph-connects none
 */
export const sortBodySchema = z
    .strictObject({
    population: specElementId(),
    keys: z
        .array(sortKeySchema)
        .min(1)
        .refine((keys) => isSequentialLocalIds(keys.map((key) => key.rank)), {
        message: '並べ替えの行の優先は 1 から欠番なしで振る',
    }),
    tieBreak: z.enum(SORT_TIE_BREAKS),
    take: z.number().int().min(1).optional(),
    derive: z.array(sortDeriveSchema).optional(),
})
    .superRefine((body, context) => {
    const seen = new Set();
    for (const [index, derive] of (body.derive ?? []).entries()) {
        if (seen.has(derive.key)) {
            context.addIssue({
                code: 'custom',
                path: ['derive', index, 'key'],
                message: '1 つのキーに決め方は 1 つまで',
            });
        }
        seen.add(derive.key);
    }
});
/**
 * 変更の中身。対象のクラスと CRUD と変える先の値。
 *
 * `to` に値が入るのは、変える先が決まっているとき（`U` で状態を進める等）である。作成
 * （`C`）と削除（`D`）は値を指さないので空になる。変える先はそのプロパティの状態遷移と
 * 突き合わせて、ありえない遷移を検出する（制約 50）。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 変更の中身（対象・CRUD・変える先）の入力スキーマ
 * @graph-connects none
 */
export const mutationBodySchema = z.strictObject({
    target: specElementId(),
    crud: z.enum(MUTATION_CRUDS),
    to: specElementId().optional(),
});
/**
 * 移行の間だけ持つ「元の規則」1 件（どの業務の L-n から起こしたか）。
 *
 * `rule` は退避した規則の番号（今の `L-25` なら 25）である。master と案件の規則の差分を、
 * 処理の差分へ機械で対応づける鍵になる。**復元が終わったら消す欄なので、値が入るのは移行の
 * 間に立てた処理だけである。**
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 処理の元になった規則（業務と規則番号の組）の入力スキーマ
 * @graph-connects none
 */
export const sourceRuleSchema = z.strictObject({
    business: specElementId(),
    rule: localElementId(),
});
/**
 * 処理が形によらず持つ欄。
 *
 * `output` はこの処理が決める値（プロパティの用語の id）で、名前の前半になる。前の版の
 * 主語（`subject`）は出力に吸収した。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 処理（Rule）の 4 形すべてに共通する欄（出力・動詞・CRUD・実装・DB 参照・出典）の zod 形
 * @graph-connects none
 */
const ruleCommonShape = {
    id: optionalSpecElementId(),
    output: specElementId(),
    verb: specElementId(),
    crud: z.enum(RULE_CRUDS),
    implementedBy: z.array(implementedBySchema).optional(),
    usesTable: z.array(usesTableSchema).optional(),
    usesColumn: z.array(usesColumnSchema).optional(),
    sourceRules: z.array(sourceRuleSchema).optional(),
};
/**
 * 処理 1 形ぶんの枝を組み立てる。形と、その形の中身のスキーマを受ける。
 *
 * 中身（`body`）は形で判別される。**`body` の側に形を持たせない** — 同じ事実を 2 箇所に
 * 置くと、`kind` と `body.kind` が食い違う入力を表現できてしまう（制約 11）。
 *
 * @param kind 処理の形
 * @param body その形の中身のスキーマ
 * @returns その形の処理の zod 入力スキーマ
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 形ごとの処理の zod 入力スキーマを組み立てる
 * @graph-connects none
 */
function ruleBranch(kind, body) {
    return z.strictObject({ ...ruleCommonShape, kind: z.literal(kind), body });
}
/**
 * 判定表の処理。入力欄・出力欄・行・重なりの扱いを中身に持つ。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 判定表の処理（Rule / kind=decisionTable）の zod 入力スキーマ
 * @graph-connects none
 */
export const decisionTableRuleSchema = ruleBranch('decisionTable', decisionTableBodySchema);
/**
 * 式の処理。段の並びと出す値を中身に持つ。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 式の処理（Rule / kind=formula）の zod 入力スキーマ
 * @graph-connects none
 */
export const formulaRuleSchema = ruleBranch('formula', formulaBodySchema);
/**
 * 並べ替えの処理。母集合と行の並びと同順位の扱いを中身に持つ。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 並べ替えの処理（Rule / kind=sort）の zod 入力スキーマ
 * @graph-connects none
 */
export const sortRuleSchema = ruleBranch('sort', sortBodySchema);
/**
 * 変更の処理。対象・CRUD・変える先を中身に持つ。
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 変更の処理（Rule / kind=mutation）の zod 入力スキーマ
 * @graph-connects none
 */
export const mutationRuleSchema = ruleBranch('mutation', mutationBodySchema);
/**
 * 処理（Rule）。形で枝を選ぶ判別ユニオン 4 枝。選んだ形に無い欄を送ると拒否する。
 *
 * `implementedBy` / `usesTable` / `usesColumn` / `sourceRules` はどれも書込時には止めない。
 *
 * - `implementedBy` に値が入るのは実装の割り当てが済んだ処理だけである。実装 200 本の割り
 *   当てには人の判断が要り、処理を先に立てられないと移行が詰まる。0 件はリリースゲートの
 *   違反 46 として出す
 * - `usesTable` / `usesColumn` に値が入るのは DB を読み書きする処理だけである。触らない処理
 *   は空にする（今の「DB を触らない理由」の自由文は持たない）
 * - `sourceRules` に値が入るのは移行の間に立てた処理だけである
 *
 * @graph-stack spec-graph
 * @graph-domain Engineering
 * @graph-business 処理（Rule）の zod 入力スキーマ。出力・動詞・形・CRUD・中身・実装・DB 参照・元の規則
 * @graph-connects none
 */
export const ruleElementSchema = z.discriminatedUnion('kind', [
    decisionTableRuleSchema,
    formulaRuleSchema,
    sortRuleSchema,
    mutationRuleSchema,
]);
//# sourceMappingURL=rule.js.map